import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import {
  Bus, ChevronLeft, ChevronRight, Maximize2, Minimize2,
} from 'lucide-react'
import { pollWhileVisible } from '../lib/pollWhileVisible'
import { EmptyState } from '../cc/EmptyState'
import { FilterChips } from '../cc/FilterChips'
import { Panel } from '../cc/Panel'
import { SearchBox } from '../cc/SearchBox'
import { SortBar } from '../cc/SortBar'
import { EYEBROW, PANEL } from '../cc/tokens'
import { TopBar } from '../cc/TopBar'
import { useSession } from '../auth/useSession'
import { useNavigate } from 'react-router-dom'
import { defaultSurfaceFor, fleetPills, pathFor, type FleetId } from '../cc/fleetNav'
import { VehicleCard, type VehicleStatus } from '../cc/VehicleCard'
import { NetradyneCameraButton } from '../cc/NetradyneCameraButton'
import { BusRouteMap } from './BusRouteMap'
import { BusAlertsFeed } from './BusAlertsFeed'
import { BusBreakdownsPanel } from './BusBreakdownsPanel'
import { BusDepotBoard } from './BusDepotBoard'
import { NagpurBusDetailPanel } from './NagpurBusDetailPanel'
import { NagpurTripAlerts } from './NagpurTripAlerts'
import { fetchBusAlertCount, fetchBusBoard, fetchBusBreakdowns, fetchBusRoutes, groupByDepot } from './useBusApi'
import type { BusBoardRow, BusBreakdown, BusDepotGroup } from './types'

const SPV = 'NAGPUR'

/** How often the Fleet panel re-polls the board/breakdowns/alert-count -- the one poll
 *  BusRouteMap, BusDepotBoard and BusBreakdownsPanel all now read from (via props/a
 *  shared refresh callback) instead of each running its own, per CLAUDE.md's live-data
 *  section. */
const FLEET_POLL_MS = 15_000

/** No update this long reads as "no signal", not confidently moving/idle -- same rule
 *  BusRouteMap.tsx's own vehicleStatus() applies to marker rendering, duplicated here
 *  (not imported) since the two files derive two different status vocabularies from it:
 *  this one maps onto VehicleCard's shared moving/idle/loading/alert/offline enum, not
 *  BusRouteMap's own local moving/idle/stale. */
const FLEET_STALE_MS = 5 * 60_000

/** Not a geofence lookup, just a label -- BusRouteMap.tsx's DEPOTS carries the real
 *  coordinates (and isn't exported); this only needs the display name for a card's
 *  "place" line. */
const DEPOT_NAME: Record<string, string> = {
  wathoda: 'Wathoda Depot',
  khapri: 'Khapri Naka (depot)',
}

/** EDMS duty join (serving/app.py's _nagpur_duty_for/_nagpur_current_trip), rendered as
 *  VehicleCard's `extra` slot -- conductor/schedule/shift/current-trip have no correct
 *  home in driver/lr/trailer/event (those labels are hardcoded to the TIMS/truck
 *  domain; `lr`'s "LR" prefix would mislabel a bus schedule code). null when this plate
 *  has no duty row today, same "render nothing extra" as every other null duty field
 *  already does elsewhere on this card. */
function nagpurDutyExtra(v: BusBoardRow) {
  const parts: string[] = []
  if (v.conductor_id) parts.push(`Cond ${v.conductor_id}`)
  if (v.schedule_code) parts.push(v.schedule_code + (v.schedule_shift ? ` (${v.schedule_shift})` : ''))
  if (v.current_trip) parts.push(`${v.current_trip.from_stage ?? '?'} → ${v.current_trip.to_stage ?? '?'}`)
  // SPEC.md's own edge case: two schedules can claim the same plate/day. schedule_code
  // above only ever shows the single primary pick -- this is where the second one
  // actually becomes visible instead of silently disappearing from the drawer.
  if (v.schedules.length > 1) parts.push(`+${v.schedules.length - 1} more schedule${v.schedules.length > 2 ? 's' : ''}`)
  if (!parts.length) return null
  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 pl-4 text-[11px] leading-snug text-[#64748B]">
      {parts.map((p, i) => (
        <span key={i} className="truncate">
          {i > 0 ? <span className="mr-1.5 text-[#CBD5E1]">·</span> : null}
          {p}
        </span>
      ))}
    </div>
  )
}

function boardVehicleStatus(row: BusBoardRow, isDown: boolean): VehicleStatus {
  if (isDown) return 'alert'
  if (row.lat == null || row.lon == null) return 'offline'
  if (row.updated_ts == null || Date.now() - row.updated_ts > FLEET_STALE_MS) return 'offline'
  return row.speed_kph != null && row.speed_kph > 3 ? 'moving' : 'idle'
}

const FLEET_STATUS_LABEL: Record<VehicleStatus, string> = {
  moving: 'Moving', idle: 'Idle', loading: 'Loading', alert: 'Down', offline: 'Offline',
}

const FLEET_FILTERS = [
  { id: 'all', label: 'ALL' },
  { id: 'moving', label: 'MOVING' },
  { id: 'idle', label: 'IDLE' },
  { id: 'down', label: 'DOWN' },
  { id: 'offline', label: 'OFFLINE' },
]

const FLEET_SORTS = [
  { id: 'status', label: 'Status' },
  { id: 'plate', label: 'Plate' },
  { id: 'speed', label: 'Speed' },
  { id: 'soc', label: 'SoC' },
]

const DEPOT_TABS: { id: 'wathoda' | 'khapri' | 'other'; label: string }[] = [
  { id: 'wathoda', label: 'Wathoda' },
  { id: 'khapri', label: 'Khapri' },
  // ~39 plates have no confident depot assignment (vehicles.csv's own provenance
  // comment) -- previously a real, silent gap: they matched neither Wathoda nor
  // Khapri and had no tab of their own, so they never appeared in the Fleet panel at
  // all ("why doesn't Fleet panel show all buses like Ultratech" -- direct
  // instruction). Ultratech's own panel has no depot-tab gating in the first place;
  // this tab is the equivalent for a fleet that does need one.
  { id: 'other', label: 'Other' },
]

function NagpurFleetDrawer({
  open, onToggle, board, boardLoaded, breakdowns, selected, onSelectVehicle,
}: {
  open: boolean
  onToggle: () => void
  board: BusBoardRow[]
  boardLoaded: boolean
  breakdowns: BusBreakdown[]
  selected: string | null
  onSelectVehicle: (id: string) => void
}): JSX.Element {
  const [depotTab, setDepotTab] = useState<'wathoda' | 'khapri' | 'other'>('wathoda')
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState('all')
  const [sort, setSort] = useState('status')

  const breakdownByVehicle = useMemo(() => {
    const m = new Map<string, BusBreakdown>()
    for (const b of breakdowns) if (!m.has(b.vehicle_id)) m.set(b.vehicle_id, b)
    return m
  }, [breakdowns])

  // home_depot_id (vehicles.csv's assigned depot), not depot_id (current presence) -- a
  // bus mid-route must stay under its own depot's tab, not disappear once it leaves the
  // radius. 'other' catches the ~39 plates with no confident depot assignment -- every
  // registered Nagpur vehicle now lands in exactly one tab, none silently dropped.
  const tabBoard = useMemo(
    () => board.filter((v) => (depotTab === 'other' ? !v.home_depot_id : v.home_depot_id === depotTab)),
    [board, depotTab],
  )
  const tabCounts = useMemo(() => {
    const counts: Record<string, number> = { wathoda: 0, khapri: 0, other: 0 }
    for (const v of board) counts[v.home_depot_id ?? 'other'] = (counts[v.home_depot_id ?? 'other'] ?? 0) + 1
    return counts
  }, [board])

  const liveCount = useMemo(
    () => tabBoard.filter((v) => v.lat != null && v.lon != null).length,
    [tabBoard],
  )

  const q = query.trim().toLowerCase()
  let rows = tabBoard.filter((v) => !q || v.vehicle_id.toLowerCase().includes(q))
    .filter((v) => {
      if (filter === 'all') return true
      const isDown = breakdownByVehicle.has(v.vehicle_id)
      return boardVehicleStatus(v, isDown) === (filter === 'down' ? 'alert' : filter)
    })
  if (sort === 'plate') {
    rows = [...rows].sort((a, b) => a.vehicle_id.localeCompare(b.vehicle_id))
  } else if (sort === 'speed') {
    rows = [...rows].sort((a, b) => (b.speed_kph ?? -1) - (a.speed_kph ?? -1))
  } else if (sort === 'soc') {
    rows = [...rows].sort((a, b) => (a.soc_pct ?? 101) - (b.soc_pct ?? 101))
  }

  return (
    <aside className={clsx(
      'flex min-h-0 shrink-0 flex-col transition-[width] duration-200',
      open ? 'w-[300px]' : 'w-[52px]',
    )}>
      <Panel className="min-h-0 flex-1" pad={false}>
        <div className="flex h-full min-h-0 flex-col">
          <div className="flex shrink-0 items-center gap-2 border-b border-[rgba(15,23,42,0.06)] px-3 py-2.5">
            <button
              type="button"
              onClick={onToggle}
              aria-label={open ? 'Collapse fleet drawer' : 'Expand fleet drawer'}
              title={open ? 'Collapse' : 'Fleet'}
              className="flex shrink-0 items-center gap-1.5 rounded-[8px] p-1 text-[#0369A1] transition-colors hover:bg-[#EEF2F7]"
            >
              <Bus size={14} />
              {open ? <ChevronLeft size={12} /> : <ChevronRight size={12} />}
            </button>
            {open && (
              <>
                <span className="text-[12px] font-semibold text-[#0F172A]">Fleet</span>
                <span className="ml-auto font-mono text-[11px] font-bold tabular-nums text-[#047857]">
                  {liveCount}
                  <span className="font-medium text-[#94A3B8]">/{tabBoard.length || '—'}</span>
                </span>
              </>
            )}
          </div>
          {open ? (
            <div className="flex min-h-0 flex-1 flex-col gap-2.5 p-3">
              <div className="flex overflow-hidden rounded-[8px] border border-[rgba(15,23,42,0.1)] text-[11px] font-medium">
                {DEPOT_TABS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    aria-pressed={depotTab === t.id}
                    onClick={() => setDepotTab(t.id)}
                    className={clsx(
                      'flex-1 px-2 py-1.5 transition-colors',
                      depotTab === t.id
                        ? 'bg-[#0369A1] text-white'
                        : 'bg-white text-[#475569] hover:bg-[#F8FAFC]',
                    )}
                  >
                    {t.label}
                    <span className={clsx('ml-1 font-mono', depotTab === t.id ? 'text-white/80' : 'text-[#94A3B8]')}>
                      {tabCounts[t.id] ?? 0}
                    </span>
                  </button>
                ))}
              </div>
              <SearchBox value={query} onChange={setQuery} placeholder="Plate…"
                         counter={`${rows.length}/${tabBoard.length}`} />
              <FilterChips
                options={FLEET_FILTERS.map((f) => ({
                  ...f,
                  count: f.id === 'all' ? tabBoard.length
                    : tabBoard.filter((v) => {
                        const isDown = breakdownByVehicle.has(v.vehicle_id)
                        return boardVehicleStatus(v, isDown) === (f.id === 'down' ? 'alert' : f.id)
                      }).length,
                }))}
                active={filter}
                onPick={setFilter}
              />
              <SortBar options={FLEET_SORTS} active={sort} onPick={setSort} />
              <div className="min-h-0 flex-1 space-y-1.5 overflow-auto pr-0.5">
                {!boardLoaded && <p className="px-1 pt-1 text-[12px] text-[#94A3B8]">Loading…</p>}
                {boardLoaded && rows.length === 0 && (
                  <EmptyState icon={<Bus size={22} />} title="No vehicles match"
                              body="Widen the filter, or clear the search." />
                )}
                {rows.map((v) => {
                  const breakdown = breakdownByVehicle.get(v.vehicle_id)
                  const isDown = !!breakdown
                  const status = boardVehicleStatus(v, isDown)
                  return (
                    <div key={v.vehicle_id} className="group relative">
                      <VehicleCard
                        plate={v.vehicle_id}
                        status={status}
                        statusLabel={FLEET_STATUS_LABEL[status]}
                        place={
                          v.depot_id ? `Near ${DEPOT_NAME[v.depot_id] ?? v.depot_id}`
                            : v.status === 'out_on_route' ? 'Out on route' : 'No recent signal'
                        }
                        // EDMS duty join (serving/app.py's _nagpur_duty_for) -- null when
                        // this plate has no duty row today, same "null, not omitted"
                        // convention the board API itself follows (AC-3).
                        driver={v.driver_name}
                        soc={v.soc_pct}
                        speed={v.speed_kph != null ? Math.round(v.speed_kph) : null}
                        exception={
                          breakdown
                            ? {
                                icon: 'power',
                                text: breakdown.category || 'down',
                                tone: breakdown.severity === 'immobilised' ? 'danger' : 'warn',
                              }
                            : v.soc_pct != null && v.soc_pct < 20
                              ? { icon: 'zap', tone: 'danger', text: `${Math.round(v.soc_pct)}% SoC` }
                              : null
                        }
                        selected={selected === v.vehicle_id}
                        onClick={() => onSelectVehicle(v.vehicle_id)}
                        extra={nagpurDutyExtra(v)}
                      />
                      <NetradyneCameraButton
                        site="mbmt"
                        vehicleNumber={v.vehicle_id}
                        className="absolute right-2 top-2 hidden group-hover:block"
                      />
                    </div>
                  )
                })}
              </div>
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-auto py-2">
              {tabBoard.map((v) => {
                const isDown = breakdownByVehicle.has(v.vehicle_id)
                const status = boardVehicleStatus(v, isDown)
                return (
                  <button
                    key={v.vehicle_id}
                    type="button"
                    onClick={() => onSelectVehicle(v.vehicle_id)}
                    title={v.vehicle_id}
                    className={clsx(
                      'mx-auto flex h-7 w-7 items-center justify-center rounded-full transition-colors hover:bg-[#F8FAFC]',
                      selected === v.vehicle_id && 'bg-[#EFF6FF]',
                    )}
                  >
                    <span className={clsx(
                      'h-2 w-2 rounded-full',
                      status === 'alert' ? 'bg-[#F43F5E]'
                        : status === 'offline' ? 'bg-[#CBD5E1]'
                          : status === 'moving' ? 'bg-[#10B981]' : 'bg-[#F59E0B]',
                    )} />
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </Panel>
    </aside>
  )
}

/**
 * Nagpur (NTSPL) bus-ops page: the route map (BusRouteMap.tsx -- depot markers, photoreal
 * toggle, every route drawn as a clickable overview with stops/live-traffic/divergence
 * colouring shown on click), and the driving-behaviour alerts feed. Route picking lives
 * entirely on the map now, not a persistent side list -- see BusRouteMap.tsx's own header
 * comment. Separate from BusOpsShell.tsx on purpose -- that component is MBMT's (Mira
 * Bhayandar) call-centre wall, a different fleet model (MbmtVehicle/actionables/breakdown
 * desk) with no Nagpur equivalent yet.
 */
export function NagpurBusOpsShell(): JSX.Element {
  const navigate = useNavigate()
  const session = useSession()
  const userLabel = session.status === 'signed-in' ? session.session.name || session.session.email : undefined

  const [depots, setDepots] = useState<BusDepotGroup[]>([])
  const [routesLoaded, setRoutesLoaded] = useState(false)
  const [routesError, setRoutesError] = useState<string | null>(null)
  const [now, setNow] = useState(() => Date.now())
  // 'alerts'/'breakdowns' reuse the same full-panel-swap mechanism 'board' already
  // established, rather than inventing a second "expand" concept -- clicking a top-strip
  // card's header opens its full detail here, in place of the map, exactly like the
  // existing Route map / Depot board switcher already does.
  const [view, setView] = useState<'map' | 'board' | 'alerts' | 'breakdowns' | 'trip-alerts'>('map')
  // Hides the Fleet drawer and the top strip so the map fills the shell -- direct
  // instruction ("give full page open map view too"). Forces view back to 'map' on
  // entry so there is nothing to be "full page" of except the map itself.
  const [fullPageMap, setFullPageMap] = useState(false)

  const [fleetOpen, setFleetOpen] = useState(true)
  const [board, setBoard] = useState<BusBoardRow[]>([])
  const [boardLoaded, setBoardLoaded] = useState(false)
  const [breakdowns, setBreakdowns] = useState<BusBreakdown[]>([])
  // Separate from boardLoaded -- board/breakdowns/alertCount all kick off together in
  // refresh() below but resolve independently, so one being loaded does not mean the
  // other is too (BusBreakdownsPanel's own former local `loaded` was tied only to its
  // own fetch; this preserves that precision now that the fetch moved up here).
  const [breakdownsLoaded, setBreakdownsLoaded] = useState(false)
  const [alertCount, setAlertCount] = useState<number | null>(null)
  const [focusVehicleId, setFocusVehicleId] = useState<string | null>(null)

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  // Set false on unmount (effect below); checked before every setState refresh() below
  // issues, the same "a fetch outlived its component" guard the old per-effect `alive`
  // local used -- pulled into a ref so refresh can be a stable useCallback reachable from
  // both the poll interval and a child's write-handler (BusBreakdownsPanel), not just
  // this one effect's own closure.
  const aliveRef = useRef(true)
  useEffect(() => () => { aliveRef.current = false }, [])

  // Guards against FLEET_POLL_MS (or a child calling this right after its own write)
  // firing a fresh round of 3 fetches on top of a round still in flight -- same bug
  // class as BusOpsShell.tsx's own refreshInFlightRef.
  const refreshInFlightRef = useRef(false)
  const refresh = useCallback(() => {
    if (refreshInFlightRef.current) return Promise.resolve()
    refreshInFlightRef.current = true
    const board = fetchBusBoard(SPV)
      .then((rows) => { if (aliveRef.current) setBoard(rows) })
      .catch(() => { /* a failed poll should not blank the previously-loaded roster */ })
      .finally(() => { if (aliveRef.current) setBoardLoaded(true) })
    const breakdowns = fetchBusBreakdowns(true)
      .then((rows) => { if (aliveRef.current) setBreakdowns(rows) })
      .catch(() => { /* same -- a stale exception chip beats none at all */ })
      .finally(() => { if (aliveRef.current) setBreakdownsLoaded(true) })
    // Count only, for the top-strip card -- BusAlertsFeed's own full-detail view (below,
    // when 'alerts' is the active view) does its own fetch/poll independently.
    // fetchBusAlertCount reads the backend's own COUNT(*), not the length of a
    // limited row fetch -- confirmed previously a real bug: len(rows) off a
    // limit=200 call silently froze this badge at exactly 200 once the true 24h
    // count passed it (confirmed live: 1,141 real alerts, badge stuck at 200).
    const alertCountP = fetchBusAlertCount(SPV, { sinceHours: 24 })
      .then((total) => { if (aliveRef.current) setAlertCount(total) })
      .catch(() => { /* a stale count beats none at all, same as breakdowns above */ })
    return Promise.allSettled([board, breakdowns, alertCountP])
      .finally(() => { refreshInFlightRef.current = false })
  }, [])

  useEffect(() => pollWhileVisible(() => void refresh(), FLEET_POLL_MS), [refresh])

  useEffect(() => {
    let live = true
    fetchBusRoutes()
      .then((routes) => { if (live) setDepots(groupByDepot(routes)) })
      .catch((e: Error) => { if (live) setRoutesError(e.message) })
      .finally(() => { if (live) setRoutesLoaded(true) })
    return () => { live = false }
  }, [])

  const totalRoutes = useMemo(() => depots.reduce((n, g) => n + g.routes.length, 0), [depots])
  // Flat list for BusRouteMap's overview layer -- it draws every route at once and no
  // longer needs the depot grouping, which only the (now-removed) side list used.
  const allRoutes = useMemo(() => depots.flatMap((g) => g.routes), [depots])
  // Also the resolution input for NagpurBusDetailPanel's route_id lookup (schedule_code
  // + home_depot_id -> route_id) -- same flat list, no second fetch.
  const selectedVehicle = useMemo(
    () => board.find((v) => v.vehicle_id === focusVehicleId) ?? null,
    [board, focusVehicleId],
  )

  const viewTitle: Record<typeof view, string> = {
    map: 'Bus route map', board: 'Depot board',
    alerts: 'Driving-behaviour alerts', breakdowns: 'Breakdowns',
    'trip-alerts': 'Alerts per trip',
  }

  const mapPanel = !routesLoaded ? (
    <div className="flex h-full items-center justify-center text-[12px] text-[#475569]">
      Loading routes…
    </div>
  ) : routesError ? (
    <div className="flex h-full items-center justify-center text-[12px] text-[#9F1239]">
      {routesError}
    </div>
  ) : totalRoutes === 0 ? (
    <div className="flex h-full items-center justify-center">
      <EmptyState
        icon={<Bus size={18} />}
        title="No routes loaded yet"
        body="Route geometry syncs from the depot route/stop master. Nothing to show until that has run."
      />
    </div>
  ) : (
    <BusRouteMap routes={allRoutes} focusVehicleId={focusVehicleId} breakdowns={breakdowns} board={board} />
  )

  // Direct instruction: "give full page open map view too" -- the map alone, no top
  // strip, no Fleet drawer, no title row. A single Minimize2 button is the only chrome
  // left, floating over the map itself so it never competes for space with it.
  if (fullPageMap) {
    return (
      <div className="relative h-full overflow-hidden bg-[#E9EEF5]">
        <div className={clsx(PANEL, 'relative h-full overflow-hidden rounded-none')}>{mapPanel}</div>
        <button
          type="button"
          onClick={() => setFullPageMap(false)}
          aria-label="Exit full-page map"
          title="Exit full-page map"
          className="absolute right-3 top-3 z-20 flex items-center gap-1.5 rounded-[8px] border border-[rgba(15,23,42,0.12)] bg-white px-2.5 py-1.5 text-[11px] font-medium text-[#475569] shadow-[0_1px_3px_rgba(15,23,42,0.12)] transition-colors hover:bg-[#F8FAFC]"
        >
          <Minimize2 size={13} />
          Exit full page
        </button>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col overflow-hidden bg-[#E9EEF5]">
      <div className="shrink-0 px-3 pt-3">
        <TopBar
          clock={new Date(now).toLocaleTimeString('en-IN', { hour12: false, timeZone: 'Asia/Kolkata' })}
          live={routesLoaded && !routesError}
          rate={`${totalRoutes} routes`}
          user={userLabel}
          fleets={fleetPills(true)}
          activeFleet="nagpur"
          onFleet={(id) => navigate(pathFor(id as FleetId, defaultSurfaceFor(id as FleetId)))}
          nav={[]}
          activeNav="live"
          onNav={() => {}}
          onSettings={() => navigate('/settings')}
        />
      </div>

      <div className="flex shrink-0 items-end justify-between px-3 pt-3">
        <div>
          <p className={EYEBROW}>Nagpur (NTSPL) · Khapri &amp; Wathoda depots</p>
          <h1 className="font-display text-[22px] font-semibold tracking-tight text-[#0F172A]">
            {viewTitle[view]}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {/* One FilterChips bar, same component and labels MBMT's own nav uses
              (.claude/work/SPEC.md addendum, AC-10) -- replaces BOTH the old
              map/board/trip-alerts button-group AND the 3-card row below (which
              used to carry alerts/breakdowns/board as clickable KPI-style cards,
              added on an earlier direct instruction to mirror Ultratech's layout;
              superseded by this one shared pattern). */}
          <FilterChips
            active={view}
            onPick={(id) => setView(id as typeof view)}
            options={[
              { id: 'map', label: 'Live ops' },
              { id: 'alerts', label: 'Alerts', count: alertCount ?? undefined },
              { id: 'breakdowns', label: 'Breakdowns', count: breakdowns.length },
              { id: 'trip-alerts', label: 'Flagged Trips' },
              { id: 'board', label: 'Depot board' },
            ]}
          />
          <button
            type="button"
            onClick={() => { setView('map'); setFullPageMap(true) }}
            title="Full-page map"
            className="flex items-center gap-1.5 rounded-[8px] border border-[rgba(15,23,42,0.12)] bg-white px-2.5 py-1.5 text-[11px] font-medium text-[#475569] shadow-[0_1px_3px_rgba(15,23,42,0.12)] transition-colors hover:bg-[#F8FAFC]"
          >
            <Maximize2 size={13} />
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 gap-3 p-3">
        <NagpurFleetDrawer
          open={fleetOpen}
          onToggle={() => setFleetOpen((o) => !o)}
          board={board}
          boardLoaded={boardLoaded}
          breakdowns={breakdowns}
          selected={focusVehicleId}
          onSelectVehicle={(id) => { setFocusVehicleId(id); setView('map') }}
        />
        <section className={clsx(PANEL, 'relative min-h-0 flex-1 overflow-hidden rounded-[18px]')}>
          {view === 'map' && mapPanel}
          {view === 'board' && (
            <BusDepotBoard
              board={board}
              boardLoaded={boardLoaded}
              onSelectVehicle={(id) => { setFocusVehicleId(id); setView('map') }}
            />
          )}
          {view === 'alerts' && <BusAlertsFeed spv={SPV} />}
          {view === 'breakdowns' && (
            <BusBreakdownsPanel breakdowns={breakdowns} loaded={breakdownsLoaded} onMutated={refresh} />
          )}
          {view === 'trip-alerts' && <NagpurTripAlerts />}
        </section>
        {view === 'map' && (
          <aside className="flex w-[300px] shrink-0 flex-col gap-3 overflow-y-auto">
            <NagpurBusDetailPanel vehicle={selectedVehicle} routes={allRoutes} />
          </aside>
        )}
      </div>
    </div>
  )
}
